import type { Key } from "react-aria-components";

import { RequestFilterTypeEnum } from "@nlc/shared-types";

import { ToggleButton, ToggleButtonGroup } from "@/components/button";
import { SearchField, SearchInput } from "@/components/search-field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";

import type { DropdownFilterType, ToggleFilterType } from "../types";

import { DROPDOWN_FILTER_OPTIONS } from "../constants/dropdownFilters";

const DESIGNER_HIDDEN_FILTERS = new Set<DropdownFilterType>([
  "queued",
  "drafts",
]);

interface ProjectRequestsFiltersProps {
  dropdownFilter: DropdownFilterType;
  isDesigner?: boolean;
  onDropdownChange: (filter: DropdownFilterType) => void;
  onSearchChange: (value: string) => void;
  onToggleChange: (filter: ToggleFilterType) => void;
  searchValue: string;
  toggleFilter: ToggleFilterType;
}

export function ProjectRequestsFilters({
  dropdownFilter,
  isDesigner = false,
  onDropdownChange,
  onSearchChange,
  onToggleChange,
  searchValue,
  toggleFilter,
}: ProjectRequestsFiltersProps) {
  const handleDropdownChange = (key: Key | null) => {
    if (key !== null) onDropdownChange(key as DropdownFilterType);
  };

  const handleToggleChange = (keys: Set<Key>) => {
    const selected = [...keys][0] as ToggleFilterType;

    if (selected) {
      onToggleChange(selected);
    }
  };

  return (
    <div className="flex flex-col gap-3 col-span-3">
      <div className="flex flex-col gap-3 lg:flex-row md:flex-row items-center">
        <Select
          aria-label="Filter requests"
          className="flex-1"
          onSelectionChange={handleDropdownChange}
          selectedKey={dropdownFilter}
        >
          <SelectButton />
          <SelectPopover>
            <SelectListBox>
              {DROPDOWN_FILTER_OPTIONS.filter(
                (option) =>
                  !isDesigner || !DESIGNER_HIDDEN_FILTERS.has(option.id),
              ).map((option) => (
                <SelectListItem id={option.id} key={option.id}>
                  {option.label}
                </SelectListItem>
              ))}
            </SelectListBox>
          </SelectPopover>
        </Select>

        <div className="overflow-x-auto">
          <ToggleButtonGroup
            aria-label="Filter by request type"
            className="shadow-xs"
            onSelectionChange={handleToggleChange}
            selectedKeys={[toggleFilter]}
            selectionMode="single"
          >
            <ToggleButton
              aria-label="Show all request types"
              className={`cursor-pointer w-16 ${
                toggleFilter === "all"
                  ? "bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover hover:text-text-white"
                  : "text-utility-gray-700 hover:bg-utility-brand-50 hover:text-utility-brand-600 hover:border-utility-brand-200"
              }`}
              color="gray"
              id="all"
              size="sm"
              variant="secondary"
            >
              All
            </ToggleButton>
            <ToggleButton
              aria-label="Show graphic requests"
              className={`cursor-pointer w-20 ${
                toggleFilter === RequestFilterTypeEnum.GRAPHIC
                  ? "bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover hover:text-text-white"
                  : "text-utility-gray-700 hover:bg-utility-brand-50 hover:text-utility-brand-600 hover:border-utility-brand-200"
              }`}
              color="gray"
              id={RequestFilterTypeEnum.GRAPHIC}
              size="sm"
              variant="secondary"
            >
              Graphic
            </ToggleButton>
            <ToggleButton
              aria-label="Show video requests"
              className={`cursor-pointer w-16 ${
                toggleFilter === RequestFilterTypeEnum.VIDEO
                  ? "bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover hover:text-text-white"
                  : "text-utility-gray-700 hover:bg-utility-brand-50 hover:text-utility-brand-600 hover:border-utility-brand-200"
              }`}
              color="gray"
              id={RequestFilterTypeEnum.VIDEO}
              size="sm"
              variant="secondary"
            >
              Video
            </ToggleButton>
            <ToggleButton
              aria-label="Show website and UI requests"
              className={`cursor-pointer w-[123px] ${
                toggleFilter === RequestFilterTypeEnum.WEBUI
                  ? "bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover hover:text-text-white"
                  : "text-utility-gray-700 hover:bg-utility-brand-50 hover:text-utility-brand-600 hover:border-utility-brand-200"
              }`}
              color="gray"
              id={RequestFilterTypeEnum.WEBUI}
              size="sm"
              variant="secondary"
            >
              Website & UI
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>

      <SearchField aria-label="Search requests" className="w-full">
        <SearchInput
          onChange={(e) => onSearchChange(e.target.value)}
          onClear={() => onSearchChange("")}
          placeholder="Search..."
          value={searchValue}
        />
      </SearchField>
    </div>
  );
}
